import React, { ReactNode, useCallback } from 'react';

import BranchesIcon from '@atlaskit/icon/glyph/bitbucket/branches';
import DepartmentIcon from '@atlaskit/icon/glyph/department';
import FrequentIcon from '@atlaskit/icon/glyph/emoji/frequent';
import { GlyphProps } from '@atlaskit/icon/types';
import Lozenge, { ThemeAppearance } from '@atlaskit/lozenge';
import { Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { useIntl } from 'src/hooks/intl';
import { ResultStatus } from 'src/types/custom-merge-checks';

import { StatusIcon } from '../status-icon';

import { messages } from './i18n';
import {
  popupStyles,
  popupTitleStyle,
  preserveWhiteSpaceStyle,
} from './styled';

export type MergeCheckPopupProps = {
  mergeCheckKey: string;
  status: ResultStatus;
  name: string | ReactNode;
  description?: string | ReactNode;
  source?: string;
  updatedOn?: string | Date | null;
  required: boolean;
  branchName: string;
  onMouseEnter?: (key: string) => void;
  onMouseLeave?: (key: string) => void;
};

export const MergeCheckPopup = ({
  mergeCheckKey,
  status,
  name,
  description,
  source,
  updatedOn,
  required,
  branchName,
  onMouseEnter,
  onMouseLeave,
}: MergeCheckPopupProps) => {
  const { formatRelative, formatMessage } = useIntl();

  const onMouseEnterEvent = useCallback(() => {
    onMouseEnter?.(mergeCheckKey);
  }, [mergeCheckKey, onMouseEnter]);

  const onMouseLeaveEvent = useCallback(() => {
    onMouseLeave?.(mergeCheckKey);
  }, [mergeCheckKey, onMouseLeave]);

  const renderStatus = useCallback(() => {
    let appearance: ThemeAppearance = 'default';
    let label: string;

    switch (status) {
      case 'PENDING':
      case 'IN-PROGRESS':
        return null;
      case 'IMPENDING':
        appearance = 'default';
        label = formatMessage(messages.waitingToRunLabel);
        break;
      case 'FAILED':
      case 'ERROR':
        if (!required) {
          appearance = 'default';
        } else {
          appearance = 'removed';
        }
        label = formatMessage(messages.didNotPassLabel);
        break;
      case 'SUCCESS':
        label = formatMessage(messages.passedLabel);
        appearance = 'success';
        break;
    }

    return (
      <Inline
        testId="mergeCheckPopupStatus"
        alignBlock="center"
        space="space.050"
      >
        <StatusIcon
          testIdPrefix="mergeCheckPopupStatusIcon"
          status={status}
          required={required}
        />
        <Lozenge appearance={appearance}>{label}</Lozenge>
      </Inline>
    );
  }, [status, required, formatMessage]);

  const renderLabelWithIcon = (
    content: string,
    IconComponent: React.ComponentClass<GlyphProps>,
    testId?: string
  ) => {
    return (
      <Inline alignBlock="center" space="space.100" testId={testId}>
        <IconComponent
          label=""
          primaryColor={token('color.text.subtle')}
          size="small"
        />
        {/* We set maxLines since this label can contain UGC */}
        <Text color="color.text.subtle" maxLines={1}>
          {content}
        </Text>
      </Inline>
    );
  };

  const renderUpdatedOn = useCallback(() => {
    // If we explicitly set updatedOn to null, we don't want to show the updatedOn label.
    if (updatedOn === null) {
      return null;
    }

    let message: string;

    // If updatedOn is undefined, this means the check result is pending.
    if (updatedOn === undefined) {
      message = formatMessage(messages.noUpdatesLabel);
    } else {
      message = formatMessage(messages.lastUpdatedLabel, {
        relativeTime: formatRelative(new Date(updatedOn)),
      });
    }

    return renderLabelWithIcon(
      message,
      FrequentIcon,
      'mergeCheckPopupUpdatedOn'
    );
  }, [formatMessage, formatRelative, updatedOn]);

  return (
    <Box
      testId={`mergeCheckPopup-${mergeCheckKey}`}
      xcss={popupStyles}
      onMouseEnter={onMouseEnterEvent}
      onMouseLeave={onMouseLeaveEvent}
    >
      <Stack space="space.200">
        <Inline spread="space-between" alignBlock="start" space="space.200">
          <Flex xcss={popupTitleStyle}>
            <Text size="large" weight="bold" maxLines={3}>
              {name}
            </Text>
          </Flex>
          {renderStatus()}
        </Inline>
        <Stack space="space.050">
          <Box xcss={preserveWhiteSpaceStyle}>
            <Text maxLines={20}>{description}</Text>
          </Box>
          {!required && (
            <Text size="small" color="color.text.subtlest">
              {formatMessage(messages.recommendedCheckLabel)}
            </Text>
          )}
        </Stack>
        <Stack space="space.200">
          <Stack space="space.050">
            {renderLabelWithIcon(branchName, BranchesIcon)}
            {source && renderLabelWithIcon(source, DepartmentIcon)}
            {renderUpdatedOn()}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};
